import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from './api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = async () => {
    try {
      setLoading(true);
      
      // Check if token exists
      if (!authAPI.isAuthenticated()) {
        setUser(null);
        setLoading(false);
        return;
      }

      // Try to get stored user first
      const storedUser = authAPI.getStoredUser();
      if (storedUser) {
        setUser(storedUser);
      }

      // Verify with server
      try {
        const response = await authAPI.getCurrentUser();
        if (response.success && response.data?.user) {
          setUser(response.data.user);
          localStorage.setItem('adminUser', JSON.stringify(response.data.user));
        } else {
          // Token invalid
          logout();
        }
      } catch (err) {
        // If server check fails but we have stored user, keep them logged in
        // This handles offline scenarios
        if (!storedUser) {
          logout();
        }
      }
    } catch (err) {
      console.error('Auth check error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authAPI.login(username, password);
      
      if (response.success && response.data?.user) {
        setUser(response.data.user);
        return { success: true };
      } else {
        throw new Error(response.message || 'Đăng nhập thất bại');
      }
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setError(null);
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;